import dayjs from "dayjs";
import { isDateComplete } from "./dateAndTime";

/**
 * Parse MM/DD/YYYY or YYYY/MM/DD (and dashed variants) for reliable comparisons.
 */
export const parseDateForComparison = (dateString) => {
  if (!dateString || !isDateComplete(dateString)) {
    return null;
  }

  const parts = dateString.split(/[-/]/).map((p) => p.trim());
  if (parts.length !== 3) {
    return null;
  }

  let year;
  let month;
  let day;

  if (parts[0].length === 4 && /^\d{4}$/.test(parts[0])) {
    [year, month, day] = parts;
  } else {
    [month, day, year] = parts;
  }

  const padded = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const parsed = dayjs(padded);
  return parsed.isValid() ? parsed : null;
};

/**
 * Validates that a date is not earlier than a reference date
 * @param {string} dateString - The date to validate (YYYY-MM-DD format)
 * @param {string} referenceDateString - The reference date (YYYY-MM-DD format)
 * @param {string} fieldName - Name of the field being validated (for error messages)
 * @param {string} referenceFieldName - Name of the reference field (for error messages)
 * @returns {string} - Error message if validation fails, empty string if valid
 */
export const validateDateNotEarlierThan = (
  dateString,
  referenceDateString,
  fieldName,
  referenceFieldName
) => {
  if (!dateString || !isDateComplete(dateString)) {
    return "";
  }
  if (!referenceDateString || !isDateComplete(referenceDateString)) {
    return "";
  }

  const date = parseDateForComparison(dateString);
  const referenceDate = parseDateForComparison(referenceDateString);

  if (!date || !referenceDate) {
    return "";
  }

  if (date.isBefore(referenceDate, "day")) {
    return `${fieldName} cannot be earlier than ${referenceFieldName}.`;
  }

  return "";
};

/**
 * Validates Date of Death is not earlier than Date of Birth
 * @param {string} dateOfDeath - Date of death (YYYY-MM-DD format)
 * @param {string} dateOfBirth - Date of birth (YYYY-MM-DD format)
 * @returns {string} - Error message if validation fails, empty string if valid
 */
export const validateDateOfDeath = (dateOfDeath, dateOfBirth) => {
  return validateDateNotEarlierThan(
    dateOfDeath,
    dateOfBirth,
    "Date of Death",
    "Date of Birth"
  );
};

/**
 * Validates Wedding Anniversary is not earlier than Date of Birth
 * @param {string} weddingAnniversary - Wedding anniversary date (YYYY-MM-DD format)
 * @param {string} dateOfBirth - Date of birth (YYYY-MM-DD format)
 * @returns {string} - Error message if validation fails, empty string if valid
 */
export const validateWeddingAnniversary = (weddingAnniversary, dateOfBirth) => {
  return validateDateNotEarlierThan(
    weddingAnniversary,
    dateOfBirth,
    "Wedding Anniversary",
    "Date of Birth"
  );
};

/**
 * Validates Divorce Date is not earlier than Date of Birth
 * @param {string} divorceDate - Divorce date (YYYY-MM-DD format)
 * @param {string} dateOfBirth - Date of birth (YYYY-MM-DD format)
 * @returns {string} - Error message if validation fails, empty string if valid
 */
export const validateDivorceDate = (divorceDate, dateOfBirth) => {
  return validateDateNotEarlierThan(
    divorceDate,
    dateOfBirth,
    "Divorce Date",
    "Date of Birth"
  );
};

/**
 * Validates Divorce Date is not earlier than Wedding Anniversary
 * @param {string} divorceDate - Divorce date (YYYY-MM-DD format)
 * @param {string} weddingAnniversary - Wedding anniversary date (YYYY-MM-DD format)
 * @returns {string} - Error message if validation fails, empty string if valid
 */
export const validateDivorceDateAfterWedding = (
  divorceDate,
  weddingAnniversary
) => {
  return validateDateNotEarlierThan(
    divorceDate,
    weddingAnniversary,
    "Divorce Date",
    "Wedding Anniversary"
  );
};

/**
 * Validates Child Birth Date is not earlier than Parent Birth Date
 * @param {string} childBirthDate - Child's date of birth (YYYY-MM-DD format)
 * @param {string} parentBirthDate - Parent's date of birth (YYYY-MM-DD format)
 * @returns {string} - Error message if validation fails, empty string if valid
 */
export const validateChildBirthDate = (childBirthDate, parentBirthDate) => {
  return validateDateNotEarlierThan(
    childBirthDate,
    parentBirthDate,
    "Child's Date of Birth",
    "Parent's Date of Birth"
  );
};

/**
 * Validates all date relationships for a primary member
 * @param {Object} memberData - Member data object containing dates
 * @returns {Object} - Object containing validation errors for each date field
 */
export const validatePrimaryMemberDates = (memberData) => {
  const errors = {
    dob: "",
    dod: "",
    weddingAnniversary: "",
  };

  const { dob, dod, weddingAnniversary } = memberData;

  // Validate Date of Death
  if (dod && isDateComplete(dod) && dob && isDateComplete(dob)) {
    errors.dod = validateDateOfDeath(dod, dob);
  }

  // Validate Wedding Anniversary
  if (
    weddingAnniversary &&
    isDateComplete(weddingAnniversary) &&
    dob &&
    isDateComplete(dob)
  ) {
    errors.weddingAnniversary = validateWeddingAnniversary(
      weddingAnniversary,
      dob
    );
  }

  return errors;
};

/**
 * Validates all date relationships for a spouse
 * @param {Object} spouseData - Spouse data object containing dates
 * @returns {Object} - Object containing validation errors for each date field
 */
export const validateSpouseDates = (spouseData) => {
  const errors = {
    dob: "",
    dod: "",
    weddingAnniversary: "",
    divorce_date: "",
  };

  const { dob, dod, weddingAnniversary, divorce_date } = spouseData;

  // Validate Date of Death
  if (dod && isDateComplete(dod) && dob && isDateComplete(dob)) {
    errors.dod = validateDateOfDeath(dod, dob);
  }

  // Validate Wedding Anniversary
  if (
    weddingAnniversary &&
    isDateComplete(weddingAnniversary) &&
    dob &&
    isDateComplete(dob)
  ) {
    errors.weddingAnniversary = validateWeddingAnniversary(
      weddingAnniversary,
      dob
    );
  }

  // Validate Divorce Date
  if (divorce_date && isDateComplete(divorce_date)) {
    // Check against Date of Birth
    if (dob && isDateComplete(dob)) {
      errors.divorce_date = validateDivorceDate(divorce_date, dob);
    }

    // Check against Wedding Anniversary (only if no error from DOB check)
    if (
      !errors.divorce_date &&
      weddingAnniversary &&
      isDateComplete(weddingAnniversary)
    ) {
      errors.divorce_date = validateDivorceDateAfterWedding(
        divorce_date,
        weddingAnniversary
      );
    }
  }

  return errors;
};

/**
 * Validates all date relationships for an additional spouse
 * @param {Object} spouseData - Additional spouse data object containing dates
 * @returns {Object} - Object containing validation errors for each date field
 */
export const validateAdditionalSpouseDates = (spouseData) => {
  return validateSpouseDates(spouseData);
};

/**
 * Validates cross-spouse date relationships
 * @param {Object} primaryData - Primary member data containing dates
 * @param {Object} spouseData - Spouse data containing dates
 * @returns {Object} - Object containing cross-validation errors for both primary and spouse
 */
export const validateCrossSpouseDates = (primaryData, spouseData) => {
  const errors = {
    primary: {
      dob: "",
      dod: "",
      weddingAnniversary: "",
    },
    spouse: {
      dob: "",
      dod: "",
      weddingAnniversary: "",
      divorce_date: "",
    },
  };

  const {
    dob: primaryDob,
    dod: primaryDod,
    weddingAnniversary: primaryWeddingAnniversary,
  } = primaryData;

  const {
    dob: spouseDob,
    dod: spouseDod,
    weddingAnniversary: spouseWeddingAnniversary,
    divorce_date: spouseDivorceDate,
  } = spouseData;

  // 1. Spouse's Date of Birth should not be after Primary Member's Wedding Anniversary
  if (
    primaryWeddingAnniversary &&
    isDateComplete(primaryWeddingAnniversary) &&
    spouseDob &&
    isDateComplete(spouseDob)
  ) {
    const error = validateDateNotEarlierThan(
      primaryWeddingAnniversary,
      spouseDob,
      "Wedding Anniversary",
      "Spouse's Date of Birth"
    );
    if (error) {
      errors.primary.weddingAnniversary = error;
      errors.spouse.dob = "Spouse's Date of Birth cannot be after Wedding Anniversary.";
    }
  }

  // 2. Primary Member's Date of Birth should not be after Spouse's Wedding Anniversary
  if (
    spouseWeddingAnniversary &&
    isDateComplete(spouseWeddingAnniversary) &&
    primaryDob &&
    isDateComplete(primaryDob)
  ) {
    const error = validateDateNotEarlierThan(
      spouseWeddingAnniversary,
      primaryDob,
      "Wedding Anniversary",
      "Date of Birth"
    );
    if (error) {
      errors.spouse.weddingAnniversary = error;
      errors.primary.dob = "Date of Birth cannot be after Spouse's Wedding Anniversary.";
    }
  }

  // Spouse DOD vs spouse DOB is validated in validateSpouseDates (same row).

  // 4. Primary Member's Date of Death should not be before Spouse's Date of Birth
  if (
    primaryDod &&
    isDateComplete(primaryDod) &&
    spouseDob &&
    isDateComplete(spouseDob)
  ) {
    const error = validateDateNotEarlierThan(
      primaryDod,
      spouseDob,
      "Date of Death",
      "Spouse's Date of Birth"
    );
    if (error) {
      errors.primary.dod = error;
    }
  }

  // 5. Spouse's Divorce Date should not be before Primary Member's Date of Birth
  if (
    spouseDivorceDate &&
    isDateComplete(spouseDivorceDate) &&
    primaryDob &&
    isDateComplete(primaryDob)
  ) {
    const error = validateDateNotEarlierThan(
      spouseDivorceDate,
      primaryDob,
      "Spouse's Divorce Date",
      "Date of Birth"
    );
    if (error) {
      errors.spouse.divorce_date = error;
    }
  }

  // 6. Spouse's Divorce Date should not be before Primary Member's Wedding Anniversary
  if (
    spouseDivorceDate &&
    isDateComplete(spouseDivorceDate) &&
    primaryWeddingAnniversary &&
    isDateComplete(primaryWeddingAnniversary)
  ) {
    const error = validateDateNotEarlierThan(
      spouseDivorceDate,
      primaryWeddingAnniversary,
      "Spouse's Divorce Date",
      "Wedding Anniversary"
    );
    if (error) {
      errors.spouse.divorce_date = error;
    }
  }

  return errors;
};

/**
 * Checks if all date validations pass
 * @param {Object} dateValidationErrors - Object containing all date validation errors
 * @returns {boolean} - True if all validations pass, false otherwise
 */
export const areAllDateValidationsPassing = (dateValidationErrors) => {
  // Check primary member errors
  const primaryErrors = Object.values(dateValidationErrors.primary || {});
  if (primaryErrors.some((error) => error && error.trim() !== "")) {
    return false;
  }

  // Check spouse errors
  const spouseErrors = Object.values(dateValidationErrors.spouse || {});
  if (spouseErrors.some((error) => error && error.trim() !== "")) {
    return false;
  }

  // Check additional spouse errors
  const additionalSpouseErrors = dateValidationErrors.additional_spouse || [];
  for (const spouseError of additionalSpouseErrors) {
    if (spouseError) {
      const errors = Object.values(spouseError);
      if (errors.some((error) => error && error.trim() !== "")) {
        return false;
      }
    }
  }

  return true;
};

